import {DOC_STATUSES} from '../constants/documentCollection';

const DEFAULT_ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'application/pdf', 'image/heic', 'image/heif'];

/**
 * Validates a selected file against allowed MIME types.
 * Returns null if valid, or an error key if invalid.
 * @param {File} file
 * @param {string[]} [allowedMimeTypes]
 * @returns {null|'unsupportedType'}
 */
export const validateFileType = (file, allowedMimeTypes = DEFAULT_ALLOWED_MIME_TYPES) => {
  if (!file.type || !allowedMimeTypes.includes(file.type)) {
    return 'unsupportedType';
  }
  return null;
};

/**
 * Validates file size against a maximum.
 * Returns null if valid, or an error key if invalid.
 * @param {File} file
 * @param {number} maxFileSizeBytes
 * @returns {null|'fileTooLarge'}
 */
export const validateFileSize = (file, maxFileSizeBytes) => {
  if (!file.size) {
    return 'emptyFile';
  }
  if (file.size > maxFileSizeBytes) {
    return 'fileTooLarge';
  }
  return null;
};

/**
 * Formats an ISO date string (YYYY-MM-DD) to DD/MM/YYYY display format.
 * @param {string} isoDate - e.g. '2022-03-01'
 * @returns {string} - e.g. '01/03/2022'
 */
export const formatPeriodDate = (isoDate) => {
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

/**
 * Derives the visible document list from raw API data + intl.
 *
 * @param {object} params
 * @param {string|number} params.protectionNumber
 * @param {Array<{slot: number, periodStart: string, periodEnd: string}>} params.yearsRequired
 * @param {object} params.statuses - e.g. { license: 'REQUIRED', insurance_history_1: 'APPROVED' }
 * @param {object} intl - react-intl intl object
 * @returns {{ baseDocs: Array, insuranceHistoryDocs: Array }}
 */
export const deriveDocuments = ({protectionNumber, yearsRequired, statuses = {}}, intl) => {
  const fmt = (id, values) => intl.formatMessage({id}, values);

  const baseDocs = [];

  // License document — always present
  baseDocs.push({
    id: 'license',
    type: 'license',
    slot: null,
    multiFile: false,
    status: statuses.license?.status || DOC_STATUSES.REQUIRED,
    files: statuses.license?.files || [],
    label: fmt('docCollection.doc.license.label'),
    description: fmt('docCollection.doc.license.description'),
  });

  // Protection document — shown based on status (hidden when NOT_REQUIRED)
  baseDocs.push({
    id: 'protection',
    type: 'protection',
    slot: null,
    multiFile: false,
    status: statuses.protection?.status || DOC_STATUSES.NOT_REQUIRED,
    files: statuses.protection?.files || [],
    label: fmt('docCollection.doc.protection.label', {protectionNumber: protectionNumber || ''}),
    description: fmt('docCollection.doc.protection.description'),
  });

  // Insurance history docs — one per slot, sorted by slot ascending (oldest period first)
  const sortedSlots = [...yearsRequired || []].sort((a, b) => a.slot - b.slot);

  const insuranceHistoryDocs = sortedSlots.map(({slot, periodStart, periodEnd}) => {
    const startLabel = formatPeriodDate(periodStart);
    const endLabel = formatPeriodDate(periodEnd);
    const key = `insurance_history_${slot}`;

    return {
      id: key,
      type: 'insurance_history',
      slot,
      multiFile: true,
      periodStart,
      periodEnd,
      status: statuses[key]?.status || DOC_STATUSES.REQUIRED,
      files: statuses[key]?.files || [],
      label: fmt('docCollection.doc.insuranceHistory.label', {periodLabel: `${startLabel} - ${endLabel}`}),
      description: fmt('docCollection.doc.insuranceHistory.description'),
    };
  });

  return {
    baseDocs: baseDocs.filter((doc) => doc.status !== DOC_STATUSES.NOT_REQUIRED),
    insuranceHistoryDocs: insuranceHistoryDocs.filter((doc) => doc.status !== DOC_STATUSES.NOT_REQUIRED),
  };
};

// --- localStorage pending uploads (persist file names across refresh) ---

const getPendingFilesKey = (token) => `doc_pending_files_${token}`;

/**
 * Get pending uploaded files from localStorage.
 * Returns: { [documentType]: [{name, uploadId?}] }
 */
export const getPendingFiles = (token) => {
  if (!token) return {};
  try {
    return JSON.parse(localStorage.getItem(getPendingFilesKey(token)) || '{}');
  } catch {
    return {};
  }
};

/**
 * Save uploaded file names to localStorage after successful upload.
 * @param {string} token
 * @param {string} documentType
 * @param {Array<{name: string, uploadId?: string}>} files
 */
export const savePendingFiles = (token, documentType, files) => {
  if (!token) return;
  const current = getPendingFiles(token);
  current[documentType] = [...current[documentType] || [], ...files];
  localStorage.setItem(getPendingFilesKey(token), JSON.stringify(current));
};

/**
 * Remove a document type from pending files (when CRM returns files for it).
 * @param {string} token
 * @param {string} documentType
 */
export const removePendingFiles = (token, documentType) => {
  if (!token) return;
  const current = getPendingFiles(token);
  delete current[documentType];
  localStorage.setItem(getPendingFilesKey(token), JSON.stringify(current));
};

// Merge localStorage pending uploads into backend statuses
export const mergeWithPendingUploads = (statuses, token) => {
  if (!token) return statuses;

  const pendingFiles = getPendingFiles(token);
  if (!Object.keys(pendingFiles).length) return statuses;

  const merged = {...statuses};

  for (const docType of Object.keys(pendingFiles)) {
    if (!merged[docType]) continue;
    // CRM already returned files — clean up localStorage
    if (merged[docType]?.files?.length > 0) {
      removePendingFiles(token, docType);
    } else {
      // Still pending — override status + inject file names
      merged[docType] = {
        ...merged[docType],
        status: merged[docType].status === DOC_STATUSES.REQUIRED ? DOC_STATUSES.IN_REVIEW : merged[docType].status,
        files: pendingFiles[docType].map((f) => f.name),
      };
    }
  }

  return merged;
};
